import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrettyLanguageName } from "../../../languages/util/prettyLanguageName";
import type { TurnLanguageProposal } from "../../util/slopifyThread";

interface LanguageSubCardProps {
  language: TurnLanguageProposal;
  fieldNames: string[];
  sourceValues: Record<string, unknown>;
  onApply: (languageId: number) => void;
  onReject: (languageId: number) => void;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const LanguageSubCard = ({
  language,
  fieldNames,
  sourceValues,
  onApply,
  onReject,
}: LanguageSubCardProps) => {
  const { t } = useTranslation();
  const prettyLanguageName = usePrettyLanguageName();
  const [expanded, setExpanded] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const statusLabelKey =
    language.status === "applied"
      ? "general.assistantScenarIa.propagation.langStatusApplied"
      : language.status === "rejected"
      ? "general.assistantScenarIa.propagation.langStatusRejected"
      : language.status === "failed"
      ? "general.assistantScenarIa.propagation.langStatusFailed"
      : "general.assistantScenarIa.propagation.langStatusPending";

  const isTerminal = language.status !== "pending";
  const langName = prettyLanguageName(language.languageId);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      onApply(language.languageId);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white mb-1">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-2 py-1 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[11px] font-medium text-gray-700">{langName}</span>
        <span className="ml-auto text-[10px] text-gray-500">{t(statusLabelKey)}</span>
        {/* Single glyph flipped in CSS: ▸/▾ render at different sizes
            (different font fallbacks). */}
        <span className={`inline-block text-gray-400 text-[10px] ${expanded ? "" : "-rotate-90"}`}>
          ▾
        </span>
      </button>

      {expanded && (
        <div className="px-2 py-2 border-t border-gray-100">
          {language.status === "failed" && (
            <div className="text-[11px] text-red-600 mb-2">
              {language.failureError || t("general.assistantScenarIa.propagation.langStatusFailed")}
            </div>
          )}
          {fieldNames.map((fieldName) => {
            const before = formatValue(sourceValues?.[fieldName]);
            const after = formatValue(language.translations?.[fieldName]);
            return (
              <div key={fieldName} className="grid grid-cols-2 gap-2 text-[11px] mb-1">
                <div>
                  <div className="text-gray-400">{fieldName}</div>
                  <div className="text-gray-500 break-words">{before || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-400">→</div>
                  <div className="text-gray-800 break-words">{after || "—"}</div>
                </div>
              </div>
            );
          })}
          {!isTerminal && (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                className="text-[11px] px-2 py-1 rounded-md bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
                disabled={isApplying}
                onClick={handleApply}
              >
                {t("general.assistantScenarIa.propagation.applyLanguage")}
              </button>
              <button
                type="button"
                className="text-[11px] px-2 py-1 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                disabled={isApplying}
                onClick={() => onReject(language.languageId)}
              >
                {t("general.assistantScenarIa.propagation.rejectLanguage")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageSubCard;
